<script lang="ts" setup>
	import {getProductDetailUrl, getSubstituteProductDetailUrl} from '@/api/api';
	import FavoriteProductButton from '@/components/table/producttable/FavoriteProductButton.vue';
	import {useRegionStore} from '@/store/region.store';
	import {getSelectedWeek, type Product} from 'dummen-shared';
	import {computed} from 'vue';

	const props = defineProps<{
		product: Product;
		showPatCode?: boolean;
		showForm?: boolean;
		showLocation?: boolean;
		mobileViewActive: boolean;
		noAvailabilityForRegionalWebSite: boolean;
	}>();

	const FOCUS_DISCONTINUED = 'DISCONTINUED';

	const selectedWeek = computed(() => getSelectedWeek());

	const availability = computed(() => {
		// Find the availability amount for the selected week for this product.
		const availability = props.product.fields.filter(field => field.key === selectedWeek.value);

		return availability.length ? availability[0].content : null;
	});

	const region = useRegionStore().getSelectedRegionUrlName();

	const getDetailUrl = () => {
		const {urlFriendlyId, urlFriendlyName, urlFriendlyProductgroup} = props.product;

		return getProductDetailUrl(region, urlFriendlyId, urlFriendlyName, urlFriendlyProductgroup);
	};

	const getSubstituteUrl = () => {
		const substituteId = props.product.substitutePatcode != null ? props.product.substitutePatcode : '';

		if (substituteId) {
			return getSubstituteProductDetailUrl(region, substituteId);
		}
	};

	const showFallbackImage = (e : Event) =>
		(e.target as HTMLImageElement).src = '/assets/images/build/fallback-product-image.jpg';
</script>

<template>
	<tr :data-rowindex="product.id">
		<td data-column="favorite">
			<FavoriteProductButton :product-id="product.id" />
		</td>

		<td data-column="image">
			<a :href="getDetailUrl()">
				<img v-if="product.image"
				     :src="product.image"
				     @error="showFallbackImage"
				     alt="product-image"
				     data-pagespeed-no-transform
				     height="48"
				     sizes="(min-width: 640px) 48px, 65px"
				     width="48" />

				<img v-else alt="product-image-fallback"
				     data-pagespeed-no-transform
				     height="48"
				     src="/assets/images/build/fallback-product-image.jpg"
				     width="48" />
			</a>
		</td>

		<th :title="product.name" data-column="variety" scope="row">
			<h2>
				<a :href="getDetailUrl()">
					<span class="label--title">
						{{ product.name }}
					</span>
					<span v-if="product.focus" class="label--new">
					  {{ product.focus }}
					</span>
				</a>

				<a v-if="product.focus === FOCUS_DISCONTINUED && product.substituteName.length > 0"
				   :href="getSubstituteUrl()">
					<span class="label--new label-discontinued">
						  REPLACEMENT: {{ product.substituteName }}
					</span>
				</a>
			</h2>
		</th>

		<td v-if="product.productgroup"
		    :title="product.productgroup"
		    data-column="group">
			<div>{{ product.productgroup }}</div>
		</td>

		<td v-if="showPatCode"
		    :title="product.patcode"
		    data-column="patcode">
			<div>{{ product.patcode }}</div>
		</td>

		<td v-if="showLocation"
		    :title="product.location?.longName ?? ''"
		    data-column="location">
			<strong>{{ product.location?.shortName ?? '' }}</strong>
		</td>

		<td v-if="showForm"
		    :title="product.form?.longName ?? ''"
		    data-column="form">
			<strong>{{ product.form?.shortName ?? '' }}</strong>
		</td>

		<td v-if="mobileViewActive" data-column="mobileAvailability">
			<span v-if="product.unknown || noAvailabilityForRegionalWebSite">
				<svg class="icon-phone" height="11" width="11">
					<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#phone"></use>
				</svg>
			</span>

			<span v-else :class="[availability ? '' : 'empty-td-placeholder']"
			      v-html="availability ?  availability : '&mdash;'">
			</span>
		</td>
	</tr>
</template>
